
import { ReactNode, useEffect } from "react";
import { NavigationBar } from "./layout/NavigationBar";
import { Footer } from "./layout/Footer";
import { OfflineIndicator } from "./offline/OfflineIndicator";
import { ScreenFilterLayer } from "./effects/ScreenFilterLayer";
import { PullToRefreshIndicator } from "./layout/PullToRefreshIndicator";
import { SkipLink } from "./accessibility/SkipLink";
import { ScreenReaderAnnouncer } from "./accessibility/ScreenReaderAnnouncer";
import { useDailyLoginPack } from "@/hooks/useDailyLoginPack";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { useUpdateManager } from "@/hooks/useUpdateManager";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { useAccessibilitySettings } from "@/hooks/useAccessibilitySettings";
import { toast } from "sonner";
import { safeReload } from "@/utils/navigation";

interface LayoutProps {
  children?: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  useDailyLoginPack();
  useKeyboardNavigation();
  useAccessibilitySettings(); // Initialize accessibility settings
  
  const { checkForUpdates } = useUpdateManager();
  
  const handleRefresh = async () => {
    try {
      const hasUpdate = await checkForUpdates();
      
      if (hasUpdate) {
        toast.success("Update available! You can install it from the notification.");
      } else {
        toast.info("App is up to date - refreshing page...");
        setTimeout(() => {
          safeReload('pull-refresh');
        }, 1000);
      }
    } catch (error) {
      toast.error("Failed to check for updates - refreshing page...");
      setTimeout(() => {
        safeReload('pull-refresh');
      }, 1000);
    }
  };

  const {
    containerRef,
    isPulling,
    isRefreshing,
    pullDistance,
    shouldTrigger
  } = usePullToRefresh({
    onRefresh: handleRefresh,
    threshold: 80,
    resistance: 2.5
  });
  
  // Mark fresh load and clear it after component mounts
  useEffect(() => {
    sessionStorage.setItem('app-fresh-load', 'true');
    const timer = setTimeout(() => {
      sessionStorage.removeItem('app-fresh-load');
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div ref={containerRef} className="min-h-screen bg-win95-teal flex flex-col p-1 overflow-x-hidden max-w-full mx-auto box-border">
      <SkipLink />
      <ScreenReaderAnnouncer />
      <OfflineIndicator />
      <ScreenFilterLayer />
      <PullToRefreshIndicator 
        pullDistance={pullDistance}
        isPulling={isPulling}
        isRefreshing={isRefreshing}
        shouldTrigger={shouldTrigger}
      />

      {/* Navigation Bar */}
      <nav className="bg-win95-gray border-2 border-win95-black mb-2" aria-label="Main navigation">
        <NavigationBar />
      </nav>

      {/* Main Content - With Windows 95 container styling */}
      <main id="main-content" tabIndex={-1} className="flex-grow outline-none" role="main">
        <div className="win95-container bg-win95-gray border-2 border-win95-black h-full">
          {children}
        </div>
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Layout;
